import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertCircle, ArrowLeft, ArrowUpRight, CloudRain, Loader2, Plus, RefreshCw, Trash2,
  Hammer, Package, FileEdit, MessageSquare, Sun, CloudLightning, Wind, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const PROJECT_TYPES = ["roofing", "remodeling", "addition", "repair", "exterior", "maintenance", "commercial", "other"];
const PROJECT_STATUSES = ["won", "scheduled", "in_progress", "on_hold", "punch_list", "closed", "cancelled"];
const PHASE_STATUSES = ["not_started", "in_progress", "blocked", "complete", "skipped"];

function StatusPill({ value }) {
  const tone = {
    won: "border-emerald-500/40 text-emerald-300",
    scheduled: "border-sky-500/40 text-sky-300",
    in_progress: "border-amber-500/40 text-amber-300",
    on_hold: "border-rose-500/40 text-rose-300",
    punch_list: "border-violet-500/40 text-violet-300",
    closed: "border-zinc-500/40 text-zinc-300",
    cancelled: "border-zinc-700 text-zinc-500",
    not_started: "border-zinc-700 text-zinc-400",
    complete: "border-emerald-500/40 text-emerald-300",
    blocked: "border-rose-500/40 text-rose-300",
    skipped: "border-zinc-700 text-zinc-500",
    high_risk: "border-rose-500/40 text-rose-300",
    alert: "border-rose-500/40 text-rose-200",
    watch: "border-amber-500/40 text-amber-300",
    wind_watch: "border-amber-500/40 text-amber-300",
    ok: "border-emerald-500/40 text-emerald-300",
    unknown: "border-zinc-700 text-zinc-500",
  }[value] || "border-zinc-700 text-zinc-400";
  return <Badge variant="outline" className={tone}>{value}</Badge>;
}

function NewProjectDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", project_type: "roofing", customer_name: "",
    city: "Austin", state: "TX", zip_code: "",
    contract_amount_usd: 0, start_date: "", target_end_date: "",
    description: "",
  });
  const submit = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const p = await crmApi.createProject({ ...form, contract_amount_usd: Number(form.contract_amount_usd || 0) });
      toast.success(`Created “${p.title}” — phases auto-seeded`);
      setOpen(false);
      onCreated?.(p);
      setForm({ title: "", project_type: "roofing", customer_name: "", city: "Austin", state: "TX", zip_code: "", contract_amount_usd: 0, start_date: "", target_end_date: "", description: "" });
    } catch (exc) {
      toast.error(exc.message || "Failed to create project");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="new-project-btn">
          <Plus className="h-4 w-4 mr-1" /> New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white max-w-xl">
        <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title" placeholder="123 Main St - Roof Replace" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                <SelectTrigger data-testid="project-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contract amount (USD)</Label>
              <Input type="number" value={form.contract_amount_usd} onChange={(e) => setForm({ ...form, contract_amount_usd: e.target.value })} data-testid="project-contract" />
            </div>
          </div>
          <div>
            <Label>Customer name</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="project-customer" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="col-span-1">
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div className="col-span-1">
              <Label>ZIP</Label>
              <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="project-zip" placeholder="78704" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>Target end date</Label>
              <Input type="date" value={form.target_end_date} onChange={(e) => setForm({ ...form, target_end_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="project-create-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create + seed phases"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WeatherIcon({ risk }) {
  if (risk === "alert" || risk === "high_risk") return <CloudLightning className="h-4 w-4 text-rose-300" />;
  if (risk === "watch") return <CloudRain className="h-4 w-4 text-amber-300" />;
  if (risk === "wind_watch") return <Wind className="h-4 w-4 text-amber-300" />;
  if (risk === "ok") return <Sun className="h-4 w-4 text-emerald-300" />;
  return <CloudRain className="h-4 w-4 text-zinc-500" />;
}

function WeatherStrip({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = async () => {
    setLoading(true);
    try {
      const res = await crmApi.projectWeather(projectId, 5);
      setItems(res?.forecast || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await crmApi.projectWeather(projectId, 5);
        if (active) setItems(res?.forecast || []);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [projectId]);
  return (
    <Card className="bg-zinc-900/40 border-zinc-800" data-testid="weather-strip">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm text-white flex items-center gap-2">
          <CloudRain className="h-4 w-4 text-sky-300" /> 5-day weather (NOAA)
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={reload} className="text-zinc-400"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-zinc-500">Forecast unavailable.</div>
        ) : (
          <div className="grid grid-cols-5 gap-2">
            {items.map((f) => (
              <div key={f.forecast_date} className="rounded-md border border-zinc-800 bg-black/60 p-2" data-testid={`wx-${f.forecast_date}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{f.forecast_date.slice(5)}</span>
                  <WeatherIcon risk={f.risk} />
                </div>
                <div className="text-xs text-white mt-1 line-clamp-1">{f.summary || "-"}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{f.precip_probability_pct}% rain · {f.wind_speed_mph}mph</div>
                <div className="mt-1"><StatusPill value={f.risk} /></div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PhasesPanel({ projectId }) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await crmApi.listPhases(projectId);
        if (active) setPhases(list || []);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [projectId]);

  const updateStatus = async (phaseId, status) => {
    try {
      const updated = await crmApi.updatePhase(phaseId, { status });
      setPhases((arr) => arr.map((p) => p.id === phaseId ? updated : p));
      toast.success(`Phase status → ${status}`);
    } catch (exc) { toast.error(exc.message || "Update failed"); }
  };

  if (loading) return <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading phases…</div>;

  return (
    <Card className="bg-zinc-900/40 border-zinc-800" data-testid="phases-panel">
      <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Hammer className="h-4 w-4 text-amber-300" /> Phases (Gantt timeline lite)</CardTitle></CardHeader>
      <CardContent>
        {phases.length === 0 ? (
          <div className="text-sm text-zinc-500">No phases yet.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {phases.map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between gap-3" data-testid={`phase-${p.sequence}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{p.sequence + 1}</span>
                    <span className="font-medium text-white">{p.name}</span>
                    {p.weather_sensitive && <Badge variant="outline" className="border-sky-500/40 text-sky-300 text-[10px]"><CloudRain className="h-3 w-3 mr-1" /> weather</Badge>}
                    <StatusPill value={p.status} />
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {(p.crew_required || []).length > 0 ? `crew: ${p.crew_required.join(", ")}` : "—"}
                    {p.planned_start && `  ·  ${p.planned_start} → ${p.planned_end || "—"}`}
                  </div>
                </div>
                <Select value={p.status} onValueChange={(v) => updateStatus(p.id, v)}>
                  <SelectTrigger className="w-[150px]" data-testid={`phase-status-${p.sequence}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{PHASE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TakeoffsPanel({ project }) {
  const [takeoffs, setTakeoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefOpen, setBriefOpen] = useState(null);

  const reload = async () => {
    setLoading(true);
    try { setTakeoffs(await crmApi.listTakeoffs(project.id) || []); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await crmApi.listTakeoffs(project.id);
        if (active) setTakeoffs(list || []);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [project.id]);

  const generate = async () => {
    try {
      const t = await crmApi.createTakeoff(project.id, { items: [], title: `${project.title} — Takeoff` });
      toast.success(`Takeoff drafted (${t.items.length} starter items)`);
      reload();
    } catch (exc) { toast.error(exc.message || "Failed"); }
  };

  return (
    <Card className="bg-zinc-900/40 border-zinc-800" data-testid="takeoffs-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm text-white flex items-center gap-2"><Package className="h-4 w-4 text-amber-300" /> Material Takeoffs</CardTitle>
        <Button size="sm" onClick={generate} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="new-takeoff-btn">
          <Plus className="h-4 w-4 mr-1" /> Generate starter takeoff
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (<div className="text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>) : takeoffs.length === 0 ? (
          <div className="text-sm text-zinc-500">No takeoffs yet. Click “Generate starter takeoff” for a starter list based on this project type.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {takeoffs.map((t) => (
              <li key={t.id} className="py-3" data-testid={`takeoff-${t.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-medium text-white">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {t.items.length} items · est. ${(t.total_estimated_usd || 0).toLocaleString()} · order target: <span className="text-amber-300">{t.order_target_date || "—"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="border-zinc-700 text-white" onClick={() => setBriefOpen(t)} data-testid={`view-brief-${t.id}`}>
                      <FileEdit className="h-3.5 w-3.5 mr-1" /> View HD brief
                    </Button>
                    <Button asChild size="sm" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid={`hd-link-${t.id}`}>
                      <a href={t.hd_deep_link} target="_blank" rel="noreferrer">
                        Open in Home Depot <ArrowUpRight className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                  </div>
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-zinc-400 cursor-pointer hover:text-white">View line items</summary>
                  <ul className="mt-2 space-y-1 text-[11px] text-zinc-400">
                    {t.items.map((it, idx) => (
                      <li key={idx} className="flex items-center justify-between gap-2 bg-black/40 rounded px-2 py-1">
                        <span>{it.description}</span>
                        <span>{it.extended_quantity || it.quantity} {it.unit} · +{it.waste_factor_pct}% waste</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={!!briefOpen} onOpenChange={() => setBriefOpen(null)}>
        <DialogContent className="bg-black border-zinc-800 text-white max-w-2xl">
          <DialogHeader><DialogTitle>Home Depot Material List brief</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-400">Paste this into Home Depot Material List Builder AI inside Pro Project Planning.</div>
          <ScrollArea className="h-[420px] rounded border border-zinc-800 bg-black p-3">
            <pre className="whitespace-pre-wrap text-xs text-white" data-testid="hd-brief-content">{briefOpen?.hd_material_list_brief}</pre>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => {
              if (briefOpen?.hd_material_list_brief) {
                navigator.clipboard?.writeText(briefOpen.hd_material_list_brief);
                toast.success("Copied to clipboard");
              }
            }} className="bg-amber-500 text-zinc-950 hover:bg-amber-400">Copy brief</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ChangeOrdersPanel({ project }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", amount_delta_usd: 0, schedule_delta_days: 0, cause: "client_request" });
  const reload = async () => setList(await crmApi.listChangeOrders(project.id) || []);

  useEffect(() => {
    let active = true;
    (async () => {
      const r = await crmApi.listChangeOrders(project.id);
      if (active) setList(r || []);
    })();
    return () => { active = false; };
  }, [project.id]);

  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await crmApi.createChangeOrder(project.id, { ...form, amount_delta_usd: Number(form.amount_delta_usd || 0), schedule_delta_days: Number(form.schedule_delta_days || 0) });
      toast.success("Change order drafted"); setOpen(false); reload();
      setForm({ title: "", description: "", amount_delta_usd: 0, schedule_delta_days: 0, cause: "client_request" });
    } catch (exc) { toast.error(exc.message || "Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await crmApi.updateChangeOrder(id, { status }); reload(); toast.success(`CO → ${status}`); }
    catch (exc) { toast.error(exc.message || "Failed"); }
  };

  return (
    <Card className="bg-zinc-900/40 border-zinc-800" data-testid="co-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm text-white flex items-center gap-2"><FileEdit className="h-4 w-4 text-amber-300" /> Change Orders</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="new-co-btn"><Plus className="h-4 w-4 mr-1" /> Draft CO</Button>
          </DialogTrigger>
          <DialogContent className="bg-black border-zinc-800 text-white">
            <DialogHeader><DialogTitle>New Change Order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="co-title" /></div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount delta (USD)</Label><Input type="number" value={form.amount_delta_usd} onChange={(e) => setForm({ ...form, amount_delta_usd: e.target.value })} data-testid="co-amount" /></div>
                <div><Label>Schedule delta (days)</Label><Input type="number" value={form.schedule_delta_days} onChange={(e) => setForm({ ...form, schedule_delta_days: e.target.value })} /></div>
              </div>
              <div>
                <Label>Cause</Label>
                <Select value={form.cause} onValueChange={(v) => setForm({ ...form, cause: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_request">client_request</SelectItem>
                    <SelectItem value="hidden_damage">hidden_damage</SelectItem>
                    <SelectItem value="code">code</SelectItem>
                    <SelectItem value="other">other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="co-create-submit">Draft</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <div className="text-sm text-zinc-500">No change orders.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {list.map((co) => (
              <li key={co.id} className="py-2 flex items-center justify-between gap-2" data-testid={`co-${co.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-white">{co.title}</span>
                    <Badge variant="outline" className={co.status === "approved" ? "border-emerald-500/40 text-emerald-300" : co.status === "rejected" ? "border-rose-500/40 text-rose-300" : "border-amber-500/40 text-amber-300"}>{co.status}</Badge>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">${(co.amount_delta_usd || 0).toLocaleString()} · +{co.schedule_delta_days || 0}d · cause: {co.cause}</div>
                </div>
                {co.status === "draft" || co.status === "pending_approval" ? (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300" onClick={() => setStatus(co.id, "approved")} data-testid={`approve-co-${co.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>
                    <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => setStatus(co.id, "rejected")}><XCircle className="h-3.5 w-3.5 mr-1" />Reject</Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ProjectDetail({ project, onBack, onDeleted }) {
  const [tab, setTab] = useState("overview");
  const remove = async () => {
    if (!window.confirm(`Delete project "${project.title}" and all phases / takeoffs / change orders?`)) return;
    try { await crmApi.deleteProject(project.id); toast.success("Project deleted"); onDeleted?.(); }
    catch (exc) { toast.error(exc.message || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="project-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="text-zinc-400 mb-2" data-testid="project-back"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> All projects</Button>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">{project.title}</h1>
            <StatusPill value={project.status} />
            <Badge variant="outline" className="border-zinc-700 text-zinc-400">{project.project_type}</Badge>
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            {project.customer_name} · {project.city}, {project.state} {project.zip_code} · contract ${(project.contract_amount_usd || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">{project.start_date || "—"} → {project.target_end_date || "—"} · PM: {project.project_manager}</div>
        </div>
        <Button variant="ghost" onClick={remove} className="text-rose-300"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
      </div>

      <WeatherStrip projectId={project.id} />
      <PhasesPanel projectId={project.id} />
      <TakeoffsPanel project={project} />
      <ChangeOrdersPanel project={project} />
    </div>
  );
}

export default function ProjectsView() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const reload = async () => {
    setLoading(true);
    try { setProjects(await crmApi.listProjects(statusFilter || undefined) || []); }
    catch (exc) { toast.error(exc.message || "Failed to load projects"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await crmApi.listProjects(statusFilter || undefined);
        if (active) setProjects(list || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load projects");
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [statusFilter]);

  if (selected) {
    return <ProjectDetail project={selected} onBack={() => setSelected(null)} onDeleted={() => { setSelected(null); reload(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="owner-projects">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="text-sm text-zinc-400 mt-1">Casey&rsquo;s workspace. Phased takeoffs, Home Depot deep-link, NOAA weather-aware scheduling, change orders.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="projects-status-filter"><SelectValue placeholder="all statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all statuses</SelectItem>
              {PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={reload} className="text-zinc-300"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          <NewProjectDialog onCreated={(p) => { reload(); setSelected(p); }} />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading projects…</div>
      ) : projects.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800"><CardContent className="py-10 text-center text-sm text-zinc-400"><AlertCircle className="h-6 w-6 text-amber-300 mx-auto mb-2" />No projects yet. Click <span className="text-amber-300">New Project</span> to create one — phases and a starter material takeoff will auto-seed.</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} className="text-left rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/40 hover:bg-zinc-900/70 transition-colors p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60" data-testid={`project-card-${p.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-medium text-white truncate">{p.title}</div>
                <StatusPill value={p.status} />
              </div>
              <div className="text-xs text-zinc-400 mt-1">{p.customer_name || "(no customer)"} · {p.project_type}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{p.city || "—"}, {p.state || "—"} {p.zip_code || ""} · ${(p.contract_amount_usd || 0).toLocaleString()}</div>
              <div className="mt-2 text-[11px] text-amber-300">Open project →</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
